import { apiClient } from '@/api/client';
import { DocumentType, SailingMode, UnitType } from '@/api/generated-types';
import type {
  SubstanceVerificationResponse,
  RankedSubstanceResponse,
  RankSubstancesRequest,
  SubstanceItemRequest,
  FormDangerousGoodsItem,
  PackagingExtractedData,
  PackagingHierarchyRequest,
  PackagingHierarchyResponse,
} from '@/api/types';

type CtuLite = { ctuId?: string | null; unitType?: number | null };

export function buildCtuUnitTypes(
  ctus: { ctuId?: string | null; unitType?: number | null }[] | undefined | null
): Map<string, number> | undefined {
  if (!ctus?.length) return undefined;
  const map = new Map<string, number>();
  for (const ctu of ctus) {
    if (ctu.ctuId && ctu.unitType != null) map.set(ctu.ctuId, ctu.unitType);
  }
  return map.size > 0 ? map : undefined;
}

export function createSubstanceItemRequest(
  dgItem: FormDangerousGoodsItem,
  ctuUnitTypes?: Map<string, number>
): SubstanceItemRequest {
  const hasOuter = dgItem.outerPackaging?.code || dgItem.outerPackaging?.description;
  const hasInner = dgItem.innerPackaging?.code || dgItem.innerPackaging?.description;
  const subsidiaryHazards = dgItem.subsidiaryHazards ?? [];

  return {
    unNumber: dgItem.unNumber || '',
    properShippingName: dgItem.properShippingName || undefined,
    hazardClass: dgItem.hazardClass || undefined,
    packingGroup: dgItem.packingGroup || undefined,
    cargoId: dgItem.cargoId || undefined,
    subsidiaryHazards,
    extractedOuter: hasOuter
      ? { code: dgItem.outerPackaging?.code, description: dgItem.outerPackaging?.description }
      : undefined,
    extractedInner: hasInner
      ? { code: dgItem.innerPackaging?.code, description: dgItem.innerPackaging?.description }
      : undefined,
    unitType: (dgItem.ctuId ? ctuUnitTypes?.get(dgItem.ctuId) : undefined) ?? undefined,
  };
}

export function buildSubstanceVerificationItems(
  dangerousGoods: FormDangerousGoodsItem[],
  ctuUnitTypes?: Map<string, number>
): SubstanceItemRequest[] {
  return dangerousGoods
    .filter((dg) => dg.unNumber?.trim() && dg.cargoId)
    .map((dg) => createSubstanceItemRequest(dg, ctuUnitTypes));
}

export async function verifySubstances(
  substanceItems: SubstanceItemRequest[],
  transactionId: string
): Promise<SubstanceVerificationResponse> {
  const { data } = await apiClient.post('/api/v1/substances/verifications', {
    substanceItems,
    transactionId,
  });
  return data;
}

export async function verifyAllSubstances(args: {
  transactionId: string;
  dangerousGoods: FormDangerousGoodsItem[];
  cargoTransportUnits: CtuLite[] | null | undefined;
}): Promise<SubstanceVerificationResponse | null> {
  const ctuUnitTypes = buildCtuUnitTypes(args.cargoTransportUnits);
  const items = buildSubstanceVerificationItems(args.dangerousGoods, ctuUnitTypes);
  if (items.length === 0) return null;
  return verifySubstances(items, args.transactionId);
}

export async function rankSubstances(
  params: RankSubstancesRequest
): Promise<RankedSubstanceResponse> {
  const { data } = await apiClient.post('/api/v1/substances/rank', params);
  return data;
}

// Frontend ergonomic wrapper over the generated PackagingHierarchyRequest:
// substanceId + unitType are required (the request is meaningless without them)
// and extracted packaging may be passed as null (converted to undefined before send).
export type GetPackingHierarchyParams = Omit<
  PackagingHierarchyRequest,
  'substanceId' | 'unitType' | 'extractedOuter' | 'extractedInner'
> & {
  substanceId: string;
  unitType: UnitType;
  extractedOuter?: PackagingExtractedData | null;
  extractedInner?: PackagingExtractedData | null;
};

function normaliseHierarchyParams(params: GetPackingHierarchyParams) {
  return {
    substanceId: params.substanceId,
    unitType: params.unitType,
    sailingMode: params.sailingMode ?? SailingMode.Cargo,
    isLq: params.isLq ?? false,
    isSample: params.isSample ?? false,
    isEq: params.isEq ?? false,
    packingMethod: params.packingMethod ?? '0',
    documentType: params.documentType ?? DocumentType.Dgd,
  };
}

export async function getPackingHierarchy(
  params: GetPackingHierarchyParams
): Promise<PackagingHierarchyResponse> {
  const { data } = await apiClient.post('/api/v1/substances/hierarchy', {
    ...normaliseHierarchyParams(params),
    extractedOuter: params.extractedOuter ?? undefined,
    extractedInner: params.extractedInner ?? undefined,
  });
  return data;
}
